import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE, ROUTES, isEnglishPath, toDaPath } from "@/config/site";
import { SEO } from "@/config/seo";
import { getProduct } from "@/data/indsatser";
import { getKommuner } from "@/data/kommuner";

const abs = (path) => `${SITE_URL}${path}`;

const Seo = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const lang = isEnglishPath(pathname) ? "en" : "da";
  const daPath = toDaPath(pathname);
  const hasQuery = Boolean(location.search && location.search.length > 1);

  // Resolve the current route key / product
  let key = ROUTES.find((r) => r.path === daPath)?.key;
  let product = null;
  if (!key && daPath.startsWith("/indsatser/")) {
    const slug = daPath.split("/")[2];
    product = getProduct(lang, slug);
    key = product ? "product" : "notfound";
  }
  if (!key) key = "notfound";

  // Meta
  let title, description, ogImage;
  if (product) {
    const label = product.subtitle ? `${product.name} – ${product.subtitle}` : product.name;
    title = `${label} | Lotusgruppen`;
    description =
      lang === "en"
        ? `${product.name}: a structured Lotusgruppen pathway. Learn about the process, framework and how to order it.`
        : `${product.name}: et struktureret Lotus-forløb. Læs om proces, rammer og hvordan det bestilles.`;
    ogImage = SEO.indsatser.og;
  } else {
    const conf = SEO[key] || SEO.notfound;
    title = conf[lang].title;
    description = conf[lang].description;
    ogImage = conf.og;
  }

  // URLs
  const canonicalDaPath = product ? daPath : ROUTES.find((r) => r.key === key)?.path ?? daPath;
  const enPath = canonicalDaPath === "/" ? "/en" : `/en${canonicalDaPath}`;
  const canonical = abs(lang === "en" ? enPath : canonicalDaPath);
  const noindex = hasQuery || key === "notfound";

  // JSON-LD
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE_URL,
    logo: abs("/icon-512.png"),
    email: SITE.contact.email,
    telephone: SITE.contact.phoneRaw,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.contact.street,
      postalCode: SITE.contact.postalCode,
      addressLocality: SITE.contact.city,
      addressCountry: SITE.contact.country,
    },
  };
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE_URL,
    inLanguage: lang === "en" ? "en" : "da",
  };
  const webpage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonical,
    inLanguage: lang === "en" ? "en" : "da",
    isPartOf: { "@type": "WebSite", url: SITE_URL, name: SITE.name },
  };

  const graphs = [org, website, webpage];

  if (product) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: product.name,
      serviceType: "Social and pedagogical support pathway",
      provider: { "@type": "Organization", name: SITE.name, url: SITE_URL },
      areaServed: { "@type": "Country", name: "Denmark" },
      description,
      url: canonical,
    });
  }

  if (key === "kommuner") {
    const faq = getKommuner(lang).faq || [];
    graphs.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <Helmet>
      <html lang={lang === "en" ? "en" : "da"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* hreflang */}
      <link rel="alternate" hrefLang="da" href={abs(canonicalDaPath)} />
      <link rel="alternate" hrefLang="en" href={abs(enPath)} />
      <link rel="alternate" hrefLang="x-default" href={abs(canonicalDaPath)} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Lotusgruppen" />
      <meta property="og:locale" content={lang === "en" ? "en_GB" : "da_DK"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json">{JSON.stringify(graphs)}</script>
    </Helmet>
  );
};

export default Seo;
